import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { Button, FormControl, MenuItem, Select, type SelectChangeEvent } from '@mui/material';
import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { ApiError } from '../../@types';
import {
  CasinoProviderActivationOperation,
  CasinoProviderRow,
  CasinoProviderSelectableGameType,
  CasinoProvidersListQueryParams,
} from '../../@types/casino';
import { QUERY_KEYS } from '../../constants';
import API_ROUTES from '../../constants/apiRoutes';
import { useModal } from '../../contexts/ModalContext';
import { useToast } from '../../contexts/ToastContext';
import { useCasinoTableQuery } from '../../hooks/useCasinoTableQuery';
import useMutateData from '../../hooks/useMutateData';
import usePermissions from '../../hooks/usePermissions';
import { createColumn } from '../../utils';
import ConfirmationModal from '../modals/ConfirmationModal';
import PositionInput from '../atoms/PositionInput';
import TableTemplate from '../templates/TableTemplate';
import { useToastErrorMessage } from '../../hooks/useToastErrorMessage';
import {
  buildCasinoProviderActivationRequest,
  useCasinoActiveAggregatorProvidersList,
  useCasinoProvidersList,
  useUpdateCasinoActiveProviders,
} from '../../queries/casino';

const DEFAULT_QUERY_PARAMS: CasinoProvidersListQueryParams = {
  page: 1,
  limit: 25,
};
const PROVIDER_GAME_TYPE_OPTIONS: CasinoProviderSelectableGameType[] = ['Originals', 'Copies'];

const useProviderPositionChange = () => {
  const { updateDataAsync } = useMutateData(
    API_ROUTES.CASINO.PROVIDER_UPDATE,
    [QUERY_KEYS.casinoProvidersList],
    'casino'
  );

  return useCallback(
    (providerId: string, position: number | null) => {
      return updateDataAsync(providerId, { position }, undefined, 'Provider updated successfully.');
    },
    [updateDataAsync]
  );
};

const createLegacyProviderColumns = (
  handlePositionChange: (providerId: string, position: number | null) => Promise<void>,
  sortableNameAndPosition = false
): GridColDef<CasinoProviderRow>[] => [
  createColumn('name', 'Name', {
    minWidth: 180,
    sortable: sortableNameAndPosition,
  }),
  createColumn('position', 'Position', {
    minWidth: 120,
    sortable: sortableNameAndPosition,
    renderCell: ({ row }: GridRenderCellParams<CasinoProviderRow>) => (
      <PositionInput
        value={row.position}
        onCommit={async (position) => {
          await handlePositionChange(row.id, position);
        }}
      />
    ),
  }),
  createColumn('_id', 'ID', {
    minWidth: 220,
    sortable: false,
  }),
  createColumn('baseApiUrl', 'Base API URL', {
    minWidth: 220,
    flex: 1,
    sortable: false,
  }),
  createColumn('baseFrbUrl', 'Base FRB URL', {
    minWidth: 220,
    flex: 1,
    sortable: false,
  }),
  createColumn('baseGameUrl', 'Base Game URL', {
    minWidth: 220,
    flex: 1,
    sortable: false,
  }),
  createColumn('imageUrl', 'Image URL', {
    minWidth: 220,
    flex: 1,
    sortable: false,
  }),
  createColumn('username', 'Username', {
    minWidth: 180,
    sortable: false,
  }),
];

const LegacyCasinoProvidersTable = () => {
  const { queryParams, paginationModel, updateQueryParams, handleSearch } =
    useCasinoTableQuery<CasinoProvidersListQueryParams>({
      defaultQuery: DEFAULT_QUERY_PARAMS,
      searchField: 'name',
      emptyFields: ['name'],
    });
  const { data: casinoProvidersData, isFetching, isLoading } = useCasinoProvidersList(queryParams);
  const handlePositionChange = useProviderPositionChange();

  const columns = useMemo<GridColDef<CasinoProviderRow>[]>(
    () => createLegacyProviderColumns(handlePositionChange),
    [handlePositionChange]
  );

  return (
    <TableTemplate
      rows={casinoProvidersData?.items || []}
      columns={columns}
      loading={isFetching || isLoading}
      rowCount={casinoProvidersData?.count || 0}
      handlePaginationModelChange={updateQueryParams}
      handleSearch={handleSearch}
      showFilter={false}
      paginationModel={paginationModel}
    />
  );
};

const SuperAdminCasinoProvidersTable = () => {
  const { enqueueSnackbar } = useToast();
  const { openModal } = useModal();
  const { handleErrorMessage } = useToastErrorMessage();
  const [updatingProviderKey, setUpdatingProviderKey] = useState<string | null>(null);
  const [selectedGameTypes, setSelectedGameTypes] = useState<Partial<Record<string, CasinoProviderSelectableGameType>>>(
    {}
  );
  const { queryParams, paginationModel, updateQueryParams, handleSearch, handleSort } =
    useCasinoTableQuery<CasinoProvidersListQueryParams>({
      defaultQuery: DEFAULT_QUERY_PARAMS,
      searchField: 'name',
      emptyFields: ['name'],
      sortDefaults: {
        sortColumn: 'sortColumn',
        sortOrder: 'sortOrder',
        defaultColumn: 'aggregatorName',
        defaultOrder: 'asc',
      },
    });
  const { data: casinoProvidersData, isFetching, isLoading } = useCasinoActiveAggregatorProvidersList(queryParams);
  const { mutateAsync: updateActiveProviders, isPending: isUpdatingActiveProvider } = useUpdateCasinoActiveProviders();
  const handlePositionChange = useProviderPositionChange();

  const getProviderActivationKey = useCallback(
    (provider: Pick<CasinoProviderRow, 'id' | 'name' | 'aggregatorName' | 'providerId'>) =>
      `${provider.name}:${provider.aggregatorName ?? ''}:${provider.providerId ?? provider.id}`,
    []
  );

  const rows = useMemo<CasinoProviderRow[]>(
    () =>
      (casinoProvidersData?.items || []).map((provider) => ({
        ...provider,
        selectedGameType: selectedGameTypes[getProviderActivationKey(provider)] ?? provider.selectedGameType,
      })),
    [casinoProvidersData?.items, getProviderActivationKey, selectedGameTypes]
  );

  const persistProviderSelection = useCallback(
    async (
      provider: CasinoProviderRow,
      operation: CasinoProviderActivationOperation,
      gameType?: CasinoProviderSelectableGameType,
      isGameTypeUpdate = false
    ) => {
      if (!provider.aggregatorName) {
        enqueueSnackbar({ message: 'Provider aggregator is missing.', variant: 'error' });
        throw new Error('Provider aggregator is missing.');
      }

      const providerKey = getProviderActivationKey(provider);

      setUpdatingProviderKey(providerKey);
      try {
        await updateActiveProviders(
          buildCasinoProviderActivationRequest({
            name: provider.name,
            aggregatorName: provider.aggregatorName,
            providerId: provider.providerId,
            gameType,
            operation,
          })
        );
        enqueueSnackbar({
          message:
            isGameTypeUpdate && gameType
              ? `Provider ${gameType.toLowerCase()} selection saved successfully.`
              : `Provider ${operation === 'add' ? 'enabled' : 'disabled'} successfully.`,
          variant: 'success',
        });
      } catch (error) {
        handleErrorMessage(error as ApiError);
        throw error;
      } finally {
        setUpdatingProviderKey(null);
      }
    },
    [enqueueSnackbar, getProviderActivationKey, handleErrorMessage, setUpdatingProviderKey, updateActiveProviders]
  );

  const handleActiveProviderChange = useCallback(
    async (provider: CasinoProviderRow) => {
      const operation = provider.status === 'enabled' ? 'remove' : 'add';
      const gameType = operation === 'add' && provider.gameType === 'Both' ? provider.selectedGameType : undefined;

      if (operation === 'add' && provider.gameType === 'Both' && !gameType) {
        enqueueSnackbar({ message: 'Choose Originals or Copies before enabling this provider.', variant: 'error' });
        return;
      }

      await persistProviderSelection(provider, operation, gameType);
    },
    [enqueueSnackbar, persistProviderSelection]
  );

  const handleProviderGameTypeChange = useCallback(
    async (provider: CasinoProviderRow, gameType: CasinoProviderSelectableGameType) => {
      const providerKey = getProviderActivationKey(provider);
      const previousGameType = provider.selectedGameType;

      setSelectedGameTypes((current) => ({ ...current, [providerKey]: gameType }));
      if (provider.status !== 'enabled') return;

      try {
        await persistProviderSelection(provider, 'add', gameType, true);
      } catch (error) {
        setSelectedGameTypes((current) => {
          const next = { ...current };

          if (previousGameType) next[providerKey] = previousGameType;
          else delete next[providerKey];

          return next;
        });
      }
    },
    [getProviderActivationKey, persistProviderSelection, setSelectedGameTypes]
  );

  const openActiveProviderConfirmationModal = useCallback(
    (provider: CasinoProviderRow) => {
      const action = provider.status === 'enabled' ? 'disable' : 'enable';
      const providerIdentity = provider.providerId ?? provider.id;

      openModal(
        <ConfirmationModal
          title={`Are you sure you want to ${action} ${provider.name} (${providerIdentity}) for this marketplace?`}
          actionButtonClose='No'
          actionButtonLabel='Yes'
          handleConfirm={() => handleActiveProviderChange(provider)}
        />
      );
    },
    [handleActiveProviderChange, openModal]
  );

  const columns = useMemo<GridColDef<CasinoProviderRow>[]>(
    () => [
      createLegacyProviderColumns(handlePositionChange, true)[0],
      createColumn('aggregatorName', 'Aggregator', {
        minWidth: 160,
        sortable: true,
      }),
      createColumn('providerId', 'Source Provider ID', {
        minWidth: 180,
        sortable: false,
        valueGetter: (_value, row, _column, _apiRef) => row.providerId ?? '—',
      }),
      createColumn('gameType', 'Game Type', {
        minWidth: 190,
        sortable: true,
        renderCell: ({ row }: GridRenderCellParams<CasinoProviderRow>) => {
          if (row.gameType !== 'Both') return row.gameType ?? '—';

          const providerIdentity = row.providerId ?? row.id;
          const selectedGameType = row.selectedGameType ?? '';

          return (
            <FormControl size='small' fullWidth>
              <Select
                value={selectedGameType}
                displayEmpty
                sx={{ '& .MuiSelect-select': { paddingLeft: '32px !important', textAlign: 'center' } }}
                inputProps={{ 'aria-label': `Game type for ${row.name} ${providerIdentity}` }}
                disabled={isUpdatingActiveProvider || updatingProviderKey === getProviderActivationKey(row)}
                renderValue={(value) => value || 'Choose type'}
                onClick={(event) => event.stopPropagation()}
                onChange={(event: SelectChangeEvent) => {
                  void handleProviderGameTypeChange(row, event.target.value as CasinoProviderSelectableGameType);
                }}
              >
                {PROVIDER_GAME_TYPE_OPTIONS.map((gameType) => (
                  <MenuItem key={gameType} value={gameType} sx={{ justifyContent: 'center' }}>
                    {gameType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        },
      }),
      ...createLegacyProviderColumns(handlePositionChange, true).slice(1),
      createColumn('status', 'Active', {
        minWidth: 120,
        sortable: true,
        renderCell: ({ row }: GridRenderCellParams<CasinoProviderRow>) => {
          const isEnabled = row.status === 'enabled';
          const isUpdating = updatingProviderKey === getProviderActivationKey(row);
          const providerIdentity = row.providerId ?? row.id;
          const needsGameType = row.gameType === 'Both' && !row.selectedGameType && !isEnabled;

          return (
            <Button
              size='small'
              variant='contained'
              color={isEnabled ? 'success' : 'error'}
              aria-label={`${isEnabled ? 'Disable' : 'Enable'} ${row.name} ${providerIdentity}`}
              disabled={isUpdating || isUpdatingActiveProvider || needsGameType}
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                openActiveProviderConfirmationModal(row);
              }}
            >
              {isEnabled ? 'On' : 'Off'}
            </Button>
          );
        },
      }),
    ],
    [
      getProviderActivationKey,
      handlePositionChange,
      handleProviderGameTypeChange,
      isUpdatingActiveProvider,
      openActiveProviderConfirmationModal,
      updatingProviderKey,
    ]
  );

  return (
    <TableTemplate
      rows={rows}
      columns={columns}
      loading={isFetching || isLoading}
      rowCount={casinoProvidersData?.count || 0}
      handlePaginationModelChange={updateQueryParams}
      handleSearch={handleSearch}
      handleSort={handleSort}
      showFilter={false}
      paginationModel={paginationModel}
    />
  );
};

const CasinoProvidersPage = () => {
  const { isSuperAdmin } = usePermissions();

  return isSuperAdmin ? <SuperAdminCasinoProvidersTable /> : <LegacyCasinoProvidersTable />;
};

export default CasinoProvidersPage;
